import { useEffect, useState, type CSSProperties } from "react"
import Lottie from "lottie-react"

import { cn } from "@/shared/lib/utils"

import haloUrl from "./assets/green_star.png"
import {
  NFT_REWARD_HALO_SIZE_PX,
  NFT_REWARD_HALO_SPIN_DURATION_S,
  NFT_REWARD_RADIUS_PX,
  NFT_REWARD_SIZES,
} from "./NftReward.config"
import { nftRewardStyles as s } from "./NftReward.styles"
import type { NftRewardProps } from "./NftReward.types"

export function NftReward({
  imageUrl,
  lottieUrl,
  alt = "",
  size = "lg",
  badge,
  halo = true,
  className,
  style,
  ...rest
}: NftRewardProps) {
  const px = NFT_REWARD_SIZES[size]

  const [lottieData, setLottieData] = useState<unknown | null>(null)
  useEffect(() => {
    if (!lottieUrl) {
      setLottieData(null)
      return
    }
    let cancelled = false
    fetch(lottieUrl)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!cancelled) setLottieData(json)
      })
      .catch(() => {
        if (!cancelled) setLottieData(null)
      })
    return () => {
      cancelled = true
    }
  }, [lottieUrl])

  const mediaStyle: CSSProperties = {
    borderRadius: NFT_REWARD_RADIUS_PX,
    boxShadow: "0 0 40px 0 rgba(0, 254, 116, 0.50)",
  }

  return (
    <div
      {...rest}
      style={{ width: px, height: px, ...style }}
      className={cn(s.root, className)}
    >
      {halo ? (
        <div
          aria-hidden
          className={s.haloWrapper}
          style={{
            width: NFT_REWARD_HALO_SIZE_PX,
            height: NFT_REWARD_HALO_SIZE_PX,
          }}
        >
          <img
            src={haloUrl}
            alt=""
            aria-hidden
            draggable={false}
            className={s.haloImage}
            style={
              {
                "--nft-reward-halo-spin": `${NFT_REWARD_HALO_SPIN_DURATION_S}s`,
              } as CSSProperties
            }
          />
        </div>
      ) : null}

      {lottieUrl && lottieData ? (
        <div
          role="img"
          aria-label={alt}
          className={cn(s.image, "overflow-hidden")}
          style={mediaStyle}
        >
          <Lottie
            animationData={lottieData}
            loop
            autoplay
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={alt}
          width={px}
          height={px}
          className={s.image}
          style={mediaStyle}
          draggable={false}
        />
      )}

      {badge ? (
        <div className={s.badgeOverlay}>
          <div className={s.badgeContent}>{badge}</div>
        </div>
      ) : null}
    </div>
  )
}
